import { Technology } from '@common/technology.enum';
import { LibrarySpecimenInput } from '../base/library-specimen.model';
import { BatchInfo, PatchBatchInfo } from '../shared/batch-info.model';

/**
 * Base interface for all library update commands.
 *
 * PATCH SEMANTICS:
 * - Optional fields (?) will be omitted from JSON if undefined.
 * - The backend will preserve existing values for omitted fields.
 * - Only fields marked as dirty/modified should be included.
 * - 'libraryId' is always required.
 *
 * MODOS DE ACTUALIZACIÓN DE SPECIMENS:
 * - FULL_REPLACEMENT: specimens[] - Reemplaza todos los specimens
 * - INCREMENTAL: addedSpecimens[] + removedLgCodes[] - Solo agrega/elimina los indicados
 * - METADATA_ONLY: ninguno de los anteriores - Solo actualiza metadatos
 */
export interface UpdateLibraryCommandBase {
  /** Library ID to update - REQUIRED */
  readonly libraryId: string;

  /** Technology type - determines which command subtype is valid */
  readonly technology?: Technology;

  /** Updated library code - Optional for PATCH */
  readonly libraryCode?: string;

  /** Compatibility-only legacy field (not required for request sending) */
  readonly editedBy?: string;

  /** Mode FULL_REPLACEMENT: Complete list of specimens (replaces all) */
  readonly specimens?: LibrarySpecimenInput[];

  /** Mode INCREMENTAL: Specimens to ADD to the library */
  readonly addedSpecimens?: LibrarySpecimenInput[];

  /** Mode INCREMENTAL: LG codes to REMOVE from the library */
  readonly removedLgCodes?: string[];
}

/**
 * Command to update an NGS library (supports PATCH partial updates)
 */
export interface UpdateLibraryNGSCommand extends UpdateLibraryCommandBase {
  readonly technology?: Technology.NGS;

  /** Cell identifier - Optional for PATCH */
  readonly cellId?: string;

  /** Updated plate identifier - Optional */
  readonly plateId?: string;

  /** Updated index identifier - Optional */
  readonly indexId?: string;
}

/**
 * Command to update a Microarray library (supports PATCH partial updates)
 */
export interface UpdateLibraryMicroarrayCommand
  extends UpdateLibraryCommandBase {
  readonly technology?: Technology.MICROARREGLOS;

  /** Updated chip type - Optional for PATCH */
  readonly chipType?: string;

  /** Updated arrangement type - Optional for PATCH */
  readonly arrangementType?: string;

  /** Updated plate name - Optional */
  readonly plateName?: string;

  /** Updated batch information - Optional. Use PatchBatchInfo for partial updates. */
  readonly batchInfo?: PatchBatchInfo | BatchInfo;
}

/**
 * Command to update an MLPA library (supports PATCH partial updates)
 */
export interface UpdateLibraryMLPACommand extends UpdateLibraryCommandBase {
  readonly technology?: Technology.MLPA;

  /** Updated creation date - Optional */
  readonly creationDate?: Date;

  /** Updated plate name - Optional */
  readonly plateName?: string;

  /** Updated equipment name - Optional */
  readonly equipment?: string;

  /** Updated batch information - Optional. Use PatchBatchInfo for partial updates. */
  readonly batchInfo?: PatchBatchInfo | BatchInfo;
}

/**
 * Command to update a Triplet Expansion library (supports PATCH partial updates)
 */
export interface UpdateLibraryTripletExpansionCommand
  extends UpdateLibraryCommandBase {
  readonly technology?: Technology.EXPANSION_TRIPLETAS;

  /** Updated creation date - Optional */
  readonly creationDate?: Date;

  /** Updated plate name - Optional */
  readonly plateName?: string;

  /** Updated equipment name - Optional */
  readonly equipment?: string;

  /** Updated batch information - Optional. Use PatchBatchInfo for partial updates. */
  readonly batchInfo?: PatchBatchInfo | BatchInfo;
}

/**
 * Union type for all library update commands
 */
export type UpdateLibraryCommand =
  | UpdateLibraryNGSCommand
  | UpdateLibraryMicroarrayCommand
  | UpdateLibraryMLPACommand
  | UpdateLibraryTripletExpansionCommand;

/**
 * Type guard for NGS update command
 */
export function isUpdateLibraryNGSCommand(
  command: UpdateLibraryCommandBase
): command is UpdateLibraryNGSCommand {
  return command.technology === Technology.NGS;
}

/**
 * Type guard for Microarray update command
 */
export function isUpdateLibraryMicroarrayCommand(
  command: UpdateLibraryCommandBase
): command is UpdateLibraryMicroarrayCommand {
  return command.technology === Technology.MICROARREGLOS;
}

/**
 * Type guard for MLPA update command
 */
export function isUpdateLibraryMLPACommand(
  command: UpdateLibraryCommandBase
): command is UpdateLibraryMLPACommand {
  return command.technology === Technology.MLPA;
}

/**
 * Type guard for Triplet Expansion update command
 */
export function isUpdateLibraryTripletExpansionCommand(
  command: UpdateLibraryCommandBase
): command is UpdateLibraryTripletExpansionCommand {
  return command.technology === Technology.EXPANSION_TRIPLETAS;
}

/**
 * Validates that a required string field is not empty.
 * @throws Error if the value is empty or blank
 */
function assertNotBlank(value: string | undefined, fieldName: string): void {
  if (value !== undefined && !value?.trim()) {
    throw new Error(`${fieldName}`);
  }
}

/**
 * Validates technology-specific fields for NGS commands.
 */
function validateNGSFields(command: UpdateLibraryNGSCommand): void {
  assertNotBlank(command.cellId, 'Cell ID cannot be empty if provided for NGS libraries');
}

/**
 * Validates technology-specific fields for Microarray commands.
 */
function validateMicroarrayFields(command: UpdateLibraryMicroarrayCommand): void {
  assertNotBlank(command.chipType, 'Chip type cannot be empty if provided for Microarray libraries');
  assertNotBlank(
    command.arrangementType,
    'Arrangement type cannot be empty if provided for Microarray libraries'
  );
}

/**
 * Validates a library update command for PATCH semantics.
 * Only validates fields that are present (not undefined).
 * @throws Error if validation fails
 */
export function validateUpdateLibraryCommand(
  command: UpdateLibraryCommand
): void {
  if (!command.libraryId?.trim()) {
    throw new Error('Library ID is required');
  }

  assertNotBlank(command.libraryCode, 'Library code cannot be empty if provided');
  assertNotBlank(command.editedBy, 'Edited by user ID is required');

  if (isUpdateLibraryNGSCommand(command)) {
    validateNGSFields(command);
  }

  if (isUpdateLibraryMicroarrayCommand(command)) {
    validateMicroarrayFields(command);
  }
}
